<template>
  <header class="header-wrapper">
    <PageContainer height="full" class="header">
      <div :class="{ 'teleport-hidden': teleportIsNotUsed }" :id="headerTeleportSlot" ref="teleportSlot"></div>

      <router-link v-if="teleportIsNotUsed" to="/" class="logo-link">
        <Logo />
      </router-link>

      <template v-if="navigationLinksVisible">
        <div class="nav-desktop">
          <NavPanel variant="primary" v-if="hasActiveProperties" :links="mainNavigationConfig" />

          <Button variant="outline" size="medium" @click="$emit('logout')" class="log-out">
            <span>Log out</span>
            <Icon name="log-out-03" />
          </Button>
        </div>

        <div class="nav-mobile">
          <IconButton @click="openMenu" variant="ghost" icon="menu-01" />
        </div>

        <BurgerMenu @logout="$emit('logout')" ref="menuDialog" />
      </template>
    </PageContainer>
  </header>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, onUpdated, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { Button, IconButton, Icon, Logo, PageContainer } from '@tallo/design-system';
import { AppRouteNames, mainNavigationConfig } from '@tallo/investor/navigation';
import { usePortfolioQuery } from '@tallo/investor/property';

import BurgerMenu from './burger-menu.vue';
import { headerTeleportSlot } from './header-teleport-slot.const';
import NavPanel from './nav-panel/nav-panel.vue';

defineEmits(['logout']);

const { hasActiveProperties } = usePortfolioQuery();

const menuDialog = ref<InstanceType<typeof BurgerMenu>>();
const openMenu = () => menuDialog.value?.open();

const route = useRoute();
const router = useRouter();

const navigationLinksVisible = computed(() => {
  switch (route.name) {
    case AppRouteNames.INVESTOR_COMPANY_ONBOARDING:
      return false;

    default:
      return true;
  }
});

const teleportSlot = ref<HTMLElement | null>(null);
const teleportIsNotUsed = ref(true);
let clearAfterNavigationCallback: () => void;

function checkIfTeleportIsUsed(): void {
  teleportIsNotUsed.value = teleportSlot.value?.children !== undefined && teleportSlot.value.children.length === 0;
}

onUpdated(() => {
  checkIfTeleportIsUsed();
});

onMounted(() => {
  checkIfTeleportIsUsed();

  window.addEventListener('resize', checkIfTeleportIsUsed);
  clearAfterNavigationCallback = router.afterEach(() => {
    nextTick(() => checkIfTeleportIsUsed()); // Wait for teleport to be used after navigation
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', checkIfTeleportIsUsed);
  clearAfterNavigationCallback();
});
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.header-wrapper {
  flex-shrink: 0;
  height: var(--t-header-height);
  background-color: var(--t-color-neutral-white);
  padding-inline: var(--t-spacing-md);
  border-bottom: 1px solid var(--t-color-neutral-300);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-link {
  line-height: 1;
}

.nav-desktop {
  display: flex;
  flex-direction: row;
  height: 100%;
  gap: var(--t-spacing-md);
}

.log-out {
  align-self: center;
}

.nav-mobile {
  display: none;
}

.teleport-hidden {
  display: none;
}

@include mobile {
  .header-wrapper {
    height: var(--t-header-height-mobile);
  }

  .nav-mobile {
    display: block;
  }

  .nav-desktop {
    display: none;
  }
}
</style>
